import React from 'react';
import classNames from 'classnames';
import moment from 'moment';
import { intlShape } from '../components/base/IntlProviderWrapper';
import TooltipWithPortal from '../components/base/Tooltip/TooltipWithPortal';
import { Icons } from '../components/base/BootstrapComponents/BootstrapComponents';

export const dateFormatter = a => {
  const {
    formatDate,
  } = intlShape;
  if (!a) {
    return '';
  }
  let d = a;
  if (typeof a === 'string') {
    const m = moment(a);
    if (!m.isValid()) {
      return a;
    }
    d = m.toDate();
  }
  return formatDate(d, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  });
};

export function dateOrDateTimeFormatter(date, onlyHour = false, onlyDate = false) {
  if (!date) {
    return '';
  }
  const currentDay = moment().day();
  const d = moment(date);
  const dateDay = moment(d).day();
  if (onlyHour) {
    return d ? d.format('HH:mm') : null;
  }
  if (onlyDate) {
    return d ? d.format('DD.MM.YY') : null;
  }
  return d ? d.format(dateDay === currentDay ? 'HH:mm' : 'DD.MM.YY') : null;
}

export const dateFormatterWithTooltip = a => {
  if (!a) {
    return '';
  }
  return (
    <TooltipWithPortal
      className="tooltip-formatter"
      tooltipContent={dateTimeFormatter(a)}
      content={
        dateOrDateTimeFormatter(a)
      }
    />
  );
};

export const timeFormatter = a => {
  const {
    formatDate,
  } = intlShape;
  if (!a) {
    return '';
  }
  return formatDate(a, {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
};

export const tooltipFormatter = a => {
  if (!a) {
    return '';
  }
  return <TooltipWithPortal className="tooltip-formatter" content={a} />;
};

export const dateTimeFormatter = a => {
  const {
    formatDate,
  } = intlShape;
  if (!a) {
    return '';
  }
  let date = a;
  if (typeof a === 'string') {
    const m = moment(a);
    if (!m.isValid()) {
      return a;
    }
    date = m.toDate();
  }
  return formatDate(date, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).replace(',', ' ');
};

export const statusFormatter = (status, statusName = 'status', statusNames = {}) => {
  const { formatMessage } = intlShape;
  const iconMap = {
    green: 'success',
    blue: 'checking',
    yellow: 'suspended',
    violet: 'return',
    red: 'denied',
    'light-blue': 'new',
    turquoise: 'withdrawn',
    gray: 'archive',
  };
  const statusClass = statusNames[status] || 'red';
  const iconName = iconMap[statusClass] || '';
  return (
    <div className={classNames('status-formatter', statusClass)}>
      <div className="status-icon">
        <Icons width={30} height={30} customIcon icon={iconName} className="button-icons" />
      </div>
      <div className="status-text">
        {formatMessage({ id: `${statusName}.${status}` })}
      </div>
    </div>
  );
};

export const userShortFIOFormatter = user => userFIOFormatter(user, true);

export const userFIOFormatter = (user, short = false) => {
  if (user) {
    if (short) {
      return `${user.last_name || user.lastName || ''} ${user.firstName ? `${user.firstName[0]}.` : ''}${user.first_name ? `${user.first_name[0]}.` : ''}${user.middleName ? `${user.middleName[0]}.` : ''}${user.middle_name ? `${user.middle_name[0]}.` : ''}`;
    }
    return `${user.lastName || user.last_name || ''} ${user.firstName || user.first_name || ''} ${user.middleName || user.middle_name || ''}`.trim();
  }
  return '';
};

export const localeFormatter = (key, value) => {
  const { formatMessage } = intlShape;
  if (typeof value === 'undefined' || value === null) return '';
  return formatMessage({ id: `${key}.${value}`, defaultMessage: ' ' });
};

export const preFormatter = a => <pre>{a}</pre>;

export const userPositionFormatter = item => (
  <div className="user-position-formatter">
    <Icons customIcon icon="user-prof" />
    <div className="user-position-formatter-info">
      <div className="user-position-formatter-position">{(item && item.position) || 'Нет информации'}</div>
    </div>
  </div>
);

export const userFullNameFormatter = item => (
  <div className="user-position-formatter">
    <Icons customIcon icon="user-prof" />
    <div className="user-position-formatter-info">
      <div className="user-position-formatter-position">{item?.full_name || 'Нет информации'}</div>
    </div>
  </div>
);

export const prevEventsFormatter = item => {
  if (!Array.isArray(item) || !item.length) return <span>Нет данных</span>;
  const events = item.reduce((prev, current) => {
    if (typeof current === 'string') {
      prev.push(current);
      return prev;
    }
    if (current?.event_types?.length) {
      current.event_types.forEach(e => {
        prev.push(localeFormatter('vehicle.state.events', `${current.vehicle_state}.${e}`));
      });
    }
    return prev;
  }, []);
  if (!events.length) return <span>Нет данных</span>;
  return (
    <div>
      {events.map((e, i) => <div className="mb-2" key={i}>{e}</div>)}
    </div>
  );
};

export const clearActivityLog = (obj = {}) => Object.keys(obj).reduce((acc, key) => {
  if (String(key).includes('_label')) return acc;
  let value = obj[key];
  if (obj && typeof obj[`${key}_label`] !== 'undefined') {
    value = obj[`${key}_label`];
  }
  acc[key] = value;
  return acc;
}, {});

export const convertActivityLog = (data, key) => {
  const { formatMessage } = intlShape;
  if (Array.isArray(data)) {
    return data.map((a, i) => convertActivityLog(a, i)).join(', ');
  }
  if (data && typeof data === 'object') {
    const clearAttributes = clearActivityLog(data);
    return Object.keys(clearAttributes).map(key => `${formatMessage({ id: key, defaultMessage: key })}: ${convertActivityLog(clearAttributes[key], key)}`).join(', ');
  }
  return data || '';
};

export const getActionLogData = activity => {
  const { formatMessage } = intlShape;
  const { properties = {} } = activity;
  const { attributes = {}, old = {}, current = {} } = properties;
  const clearAttributes = clearActivityLog(attributes);
  return Object.keys(clearAttributes).map(key => {
    const keyName = key;
    const keyValue = key;
    return ({
      name: formatMessage({ id: keyName, defaultMessage: keyName }),
      prev: convertActivityLog(old[keyValue], keyValue),
      new: convertActivityLog(clearAttributes[keyValue], keyValue),
      current: convertActivityLog(current?.[keyValue], keyValue),
    });
  });
};

export const infoFormatter = (label, value) => (
  <div className="info-formatter">
    <div className="info-formatter-label">{label}</div>
    <div className="info-formatter-value">{value}</div>
  </div>
);
